import React, { useEffect, useRef, useState } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { hiresAPI } from '../services/api.ts';
import { mapService } from '../services/mapService.ts';

// Declare Google Maps types
declare global {
  interface Window {
    google: any;
    googleMapsLoaded: boolean;
  }
}

// Google Maps loader function - now uses the map service
const loadGoogleMaps = (): Promise<void> => {
  return mapService.waitForGoogleMaps();
};

interface Hire {
  id: string;
  equipment_id: string;
  supplier_id: string;
  site_id: string;
  price: number;
  pricing_term: 'fixed' | 'day' | 'week' | 'month' | 'year';
  price_per_day?: number | string;
  hire_start_date: string;
  hire_end_date: string;
  hire_duration_days: number;
  total_cost: number;
  hire_status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  equipment_name: string;
  equipment_category: string;
  unit_of_measure?: string;
  power?: string;
  equipment_image_url?: string;
  supplier_name: string;
  supplier_email?: string;
  supplier_phone?: string;
  site_name: string;
  site_address?: string;
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_city?: string;
  site_county?: string;
  site_postcode?: string;
  site_country?: string;
  job_number?: string;
  creator_name?: string;
  creator_profile_picture?: string;
}

const HiresMapPage: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [hires, setHires] = useState<Hire[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  // Fetch hires data
  useEffect(() => {
    const fetchHires = async () => {
      try {
        setLoading(true);
        // Get all hires (not paginated) for the map
        const response = await hiresAPI.getAll(1, 1000);
        setHires(response.data.hires || []);
      } catch (err) {
        console.error('Error fetching hires:', err);
        setError('Failed to load hires data');
      } finally {
        setLoading(false);
      }
    };

    fetchHires();
  }, []);

  useEffect(() => {
    console.log('Map useEffect triggered:', { hiresLength: hires.length, hasGoogle: !!window.google });
    if (hires.length === 0) {
      console.log('Skipping map initialization: no hires data');
      return;
    }

    const initializeMap = async () => {
      console.log('Initializing map...', { 
        hasGoogle: !!window.google, 
        hasMaps: !!(window.google && window.google.maps), 
        hasMapRef: !!mapRef.current 
      });
      
      setMapLoading(true);
      
      if (window.google && window.google.maps && mapRef.current) {
        try {
          console.log('Creating Google Map...');
          // Create map centered on UK
          const map = new window.google.maps.Map(mapRef.current, {
            zoom: 6,
            center: { lat: 51.5074, lng: -0.1278 }, // London center (better for UK)
            mapTypeId: 'roadmap'
          });

          // Filter hires based on showCompleted toggle
          const filteredHires = showCompleted 
            ? hires 
            : hires.filter(hire => hire.hire_status !== 'completed');
          console.log('Processing hires:', hires.length, 'Filtered hires:', filteredHires.length, 'Show completed:', showCompleted);
          
          // Group filtered hires by postcode to handle clustering
          const hiresByPostcode = new Map<string, Hire[]>();
          filteredHires.forEach(hire => {
            console.log('Hire postcode:', hire.site_postcode, 'Site name:', hire.site_name, 'Status:', hire.hire_status);
            const postcode = hire.site_postcode || 'UNKNOWN';
            if (!hiresByPostcode.has(postcode)) {
              hiresByPostcode.set(postcode, []);
            }
            hiresByPostcode.get(postcode)!.push(hire);
          });
          
          console.log('Hires grouped by postcode:', Array.from(hiresByPostcode.entries()));

          // Create markers for each postcode using Promise.all for better performance
          const geocoder = new window.google.maps.Geocoder();
          const bounds = new window.google.maps.LatLngBounds();
          const markers: any[] = [];

          // Process all postcodes in parallel
          const geocodePromises = Array.from(hiresByPostcode.entries()).map(async ([postcode, postcodeHires]) => {
            if (postcodeHires.length === 0) return null;

            // Try to build a better address for geocoding
            let address = 'United Kingdom';
            if (postcode !== 'UNKNOWN') {
              address = `${postcode}, UK`;
            } else {
              // Try to use site address information if available
              const firstHire = postcodeHires[0];
              if (firstHire.site_address) {
                address = `${firstHire.site_address}, UK`;
              } else if (firstHire.site_name) {
                // Try to make the site name more specific for geocoding
                address = `${firstHire.site_name}, Bath, UK`;
              }
            }
            
            console.log(`Geocoding address: ${address} for ${postcodeHires.length} hires`);
            
            return new Promise<any>((resolve) => {
              geocoder.geocode({ address }, (results: any, status: any) => {
                console.log(`Geocoding result for ${address}:`, status, results);
                if (status === 'OK' && results[0]) {
                  const location = results[0].geometry.location;
                  
                  // Create custom marker icon with hover effect
                  const markerIcon = {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
                          </filter>
                        </defs>
                        <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="#1E40AF" stroke-width="2" filter="url(#shadow)"/>
                        <circle cx="20" cy="20" r="16" fill="#60A5FA" opacity="0.3"/>
                        <text x="20" y="26" text-anchor="middle" fill="white" font-family="Arial" font-size="12" font-weight="bold" pointer-events="none">
                          ${postcodeHires.length}
                        </text>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20)
                  };

                  // Create regular Marker
                  const marker = new window.google.maps.Marker({
                    position: location,
                    map: map,
                    icon: markerIcon,
                    title: `${postcodeHires.length} hire${postcodeHires.length > 1 ? 's' : ''} at ${address}`
                  });

                  // Create info window content
                  const infoContent = `
                    <div style="max-width: 300px; padding: 10px;">
                      <h3 style="margin: 0 0 10px 0; color: #1F2937; font-size: 16px;">
                        ${postcodeHires.length} Hire${postcodeHires.length > 1 ? 's' : ''} at ${address}
                      </h3>
                      <div style="max-height: 200px; overflow-y: auto;">
                        ${postcodeHires.map(hire => `
                          <div style="border-bottom: 1px solid #E5E7EB; padding: 8px 0; margin-bottom: 8px;">
                            <div style="display: flex; align-items: center; margin-bottom: 4px;">
                              ${hire.equipment_image_url ? 
                                `<img src="${hire.equipment_image_url}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px; margin-right: 8px;">` :
                                `<div style="width: 40px; height: 40px; background: #F3F4F6; border-radius: 4px; margin-right: 8px; display: flex; align-items: center; justify-content: center;">
                                  <span style="font-size: 20px;">🔧</span>
                                </div>`
                              }
                              <div>
                                <div style="font-weight: 600; color: #1F2937; font-size: 14px;">${hire.equipment_name}</div>
                                <div style="color: #6B7280; font-size: 12px;">${hire.equipment_category}</div>
                              </div>
                            </div>
                            <div style="font-size: 12px; color: #6B7280;">
                              <div><strong>Status:</strong> ${hire.hire_status.charAt(0).toUpperCase() + hire.hire_status.slice(1)}</div>
                              <div><strong>Duration:</strong> ${hire.hire_duration_days} days</div>
                              <div><strong>Supplier:</strong> ${hire.supplier_name}</div>
                              <div><strong>Site:</strong> ${hire.site_name}</div>
                              ${hire.job_number ? `<div><strong>Job:</strong> ${hire.job_number}</div>` : ''}
                            </div>
                          </div>
                        `).join('')}
                      </div>
                    </div>
                  `;

                  // Create info window
                  const infoWindow = new window.google.maps.InfoWindow({
                    content: infoContent
                  });

                  // Add click listener
                  marker.addListener('click', () => {
                    infoWindow.open(map, marker);
                  });

                  // Add hover effects with animation
                  marker.addListener('mouseover', () => {
                    marker.setIcon({
                      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                              <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="rgba(0,0,0,0.4)"/>
                            </filter>
                            <animate id="pulse" attributeName="r" values="20;25;20" dur="1.5s" repeatCount="indefinite"/>
                          </defs>
                          <circle cx="25" cy="25" r="22" fill="#1D4ED8" stroke="#1E3A8A" stroke-width="3" filter="url(#shadow)"/>
                          <circle cx="25" cy="25" r="20" fill="#3B82F6" opacity="0.4" animate="pulse"/>
                          <text x="25" y="32" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold" pointer-events="none">
                            ${postcodeHires.length}
                          </text>
                        </svg>
                      `),
                      scaledSize: new window.google.maps.Size(50, 50),
                      anchor: new window.google.maps.Point(25, 25)
                    });
                  });

                  marker.addListener('mouseout', () => {
                    marker.setIcon(markerIcon);
                  });

                  resolve({ marker, location });
                } else {
                  console.warn(`Geocoding failed for ${postcode}: ${status}`);
                  resolve(null);
                }
              });
            });
          });

          // Wait for all geocoding to complete
          const results = await Promise.all(geocodePromises);
          
          // Add all successful markers to bounds and array
          results.forEach(result => {
            if (result) {
              markers.push(result.marker);
              bounds.extend(result.location);
            }
          });

          // Fit map to bounds if we have markers
          if (markers.length > 0) {
            // Add padding by extending the bounds
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latDiff = ne.lat() - sw.lat();
            const lngDiff = ne.lng() - sw.lng();
            
            // Extend bounds by 50% on each side for more context
            bounds.extend(new window.google.maps.LatLng(ne.lat() + latDiff * 0.5, ne.lng() + lngDiff * 0.5));
            bounds.extend(new window.google.maps.LatLng(sw.lat() - latDiff * 0.5, sw.lng() - lngDiff * 0.5));
            
            map.fitBounds(bounds);
            
            // Set a maximum zoom level to prevent zooming in too close
            const listener = window.google.maps.event.addListener(map, 'idle', () => {
              if (map.getZoom() > 12) {
                map.setZoom(12);
              }
              window.google.maps.event.removeListener(listener);
            });
          } else {
            // Fallback to default view if no markers
            map.setCenter({ lat: 51.5074, lng: -0.1278 });
            map.setZoom(9);
          }

        } catch (error) {
          console.error('Error initializing map:', error);
        } finally {
          setMapLoading(false);
        }
      } else {
        setMapLoading(false);
      }
    };

    // Load Google Maps API
    console.log('Loading Google Maps...');
    loadGoogleMaps()
      .then(() => {
        console.log('Google Maps loaded, initializing map...');
        // If Google Maps is already loaded, initialize immediately
        if (window.google && window.google.maps) {
          initializeMap();
        } else {
          // Wait a bit more for the API to fully initialize
          setTimeout(() => {
            initializeMap();
          }, 500);
        }
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error);
        setMapLoading(false);
      });
  }, [hires, showCompleted]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <WrenchScrewdriverIcon className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading hires data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <WrenchScrewdriverIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Hires Map</h1>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold dark:text-orange-500 text-gray-900">
                {showCompleted ? 'All Equipment Hires' : 'Active Equipment Hires'} 
                ({hires.filter(hire => showCompleted || hire.hire_status !== 'completed').length} of {hires.length} total)
              </h2>
              <p className="text-sm text-gray-600 dark:text-neutral mt-1">
                {showCompleted ? 'Showing all hires including completed ones' : 'Showing only active hires (excluding completed)'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-neutral">
                  Show Completed
                </span>
              </label>
              <div className="text-sm text-gray-600 dark:text-neutral">
                Click markers to view details
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div 
              ref={mapRef} 
              className="w-full h-96 rounded-lg border border-gray-300"
              style={{ minHeight: '500px' }}
            />
            {mapLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600 mx-auto mb-2 animate-spin" />
                  <p className="text-gray-600 text-sm">Initializing map...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiresMapPage;
